// src/components/Admin/CreateAdminUserForm.js
// UPDATED to handle password link response - June 29, 2025

import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Box, Paper, Typography, TextField, Button, CircularProgress, Alert } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';

const CreateAdminUserForm = () => {
    const { isAdmin } = useAuth();
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState(''); // State for the success message with link

    const handleCreateAdmin = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage(''); // Clear previous success message

        if (!fullName || !email) {
            setError('Full Name and Email are required.');
            return;
        }

        if (!isAdmin) {
            toast.error("You do not have permission to perform this action.");
            return;
        }

        setIsSubmitting(true);
        const functions = getFunctions();
        // This function name matches the one already in your adminServices.js
        const adminCreateAdminUser = httpsCallable(functions, 'adminCreateAdminUser');

        try {
            const result = await adminCreateAdminUser({ fullName, email });
            
            if (result.data.success) {
                // The backend returns a message with the link, let's display it
                setSuccessMessage(result.data.message);
                toast.success(`Admin user ${email} created successfully!`);
                setFullName('');
                setEmail('');
            } else {
                throw new Error(result.data.error || 'An unknown error occurred.');
            }
        } catch (err) {
            console.error("Error creating admin user:", err);
            const errorMessage = err.message || "An unexpected error occurred. Please check the logs.";
            setError(errorMessage);
            toast.error(`Failed to create admin user: ${errorMessage}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Paper component="form" onSubmit={handleCreateAdmin} sx={{ p: 3, maxWidth: 600 }} elevation={0}>
            <Typography variant="h6" gutterBottom>
                Create a New Administrator
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                A new admin user will be created. You must manually provide the generated password setup link to the new user.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                    label="Full Name"
                    variant="outlined"
                    fullWidth
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    disabled={isSubmitting}
                    required
                />
                <TextField
                    label="Email Address"
                    variant="outlined"
                    type="email"
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubmitting}
                    required
                />
                {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
                {successMessage && <Alert severity="success" sx={{ mt: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{successMessage}</Alert>}
                <Box sx={{ mt: 2, position: 'relative' }}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                        fullWidth
                    >
                        Create Admin User
                    </Button>
                    {isSubmitting && (
                        <CircularProgress
                            size={24}
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                marginTop: '-12px',
                                marginLeft: '-12px',
                            }}
                        />
                    )}
                </Box>
            </Box>
        </Paper>
    );
};

export default CreateAdminUserForm;